import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingContext } from '../LoadingContext';
import { AuthContext } from '../AuthContext';
import Loading from './Loading';
import Swal from 'sweetalert2';
import './AdminReviewManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminReviewManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0,
    averageRating: 0
  });
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedReviewModal, setSelectedReviewModal] = useState(null);

  const { setIsLoading } = useContext(LoadingContext);
  const { authFetch } = useContext(AuthContext);
  const navigate = useNavigate();
  const [ratingFilter, setRatingFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const isReviewUpdated = (review) => {
    return review.updatedAt && review.updatedAt !== review.createdAt;
  };

  const shouldShowUpdatedBadge = (review) => {
    return isReviewUpdated(review) && review.status === 'Pending';
  };

  const getDisplayDate = (review) => {
    return review.updatedAt || review.createdAt;
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/Reviews/all`);

      if (response.ok) {
        const data = await response.json();
        const allReviews = data.reviews || [];
        calculateStats(allReviews);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchReviews = async () => {
    setIsLoading(true);
    setLoading(true);
    try {
      let endpoint = `${API_BASE_URL}/Reviews/all`;
      let queryParams = new URLSearchParams();

      if (activeTab !== 'all') {
        queryParams.append('status', activeTab);
      }

      queryParams.append('sortBy', 'CreatedAt');
      queryParams.append('sortDescending', 'true');

      if (queryParams.toString()) {
        endpoint += `?${queryParams.toString()}`;
      }

      const response = await authFetch(endpoint);

      if (!response.ok) {
        if (response.status === 404) {
          setReviews([]);
          setFilteredReviews([]);
          setError(null);
        } else {
          throw new Error('Failed to fetch reviews');
        }
      } else {
        const data = await response.json();
        const reviewsData = data.reviews || [];

        setReviews(reviewsData);
        setFilteredReviews(reviewsData);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
      setReviews([]);
      setFilteredReviews([]);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const calculateStats = (allReviews) => {
    const stats = {
      pending: 0,
      approved: 0,
      rejected: 0,
      total: allReviews.length,
      averageRating: 0
    };

    let totalRating = 0;
    allReviews.forEach(review => {
      switch (review.status) {
        case 'Pending':
          stats.pending++;
          break;
        case 'Approved':
          stats.approved++;
          totalRating += review.rating;
          break;
        case 'Rejected':
          stats.rejected++;
          break;
        default:
          break;
      }
    });

    if (stats.approved > 0) {
      stats.averageRating = (totalRating / stats.approved).toFixed(1);
    }

    setStats(stats);
  };

  useEffect(() => {
    let result = [...reviews];

    if (ratingFilter) {
      result = result.filter(review => review.rating === parseInt(ratingFilter));
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          const aDate = a.updatedAt ? new Date(a.updatedAt) : new Date(a.createdAt);
          const bDate = b.updatedAt ? new Date(b.updatedAt) : new Date(b.createdAt);
          return bDate - aDate;
        case 'oldest':
          const aDateOld = a.updatedAt ? new Date(a.updatedAt) : new Date(a.createdAt);
          const bDateOld = b.updatedAt ? new Date(b.updatedAt) : new Date(b.createdAt);
          return aDateOld - bDateOld;
        case 'highest':
          return b.rating - a.rating;
        case 'lowest':
          return a.rating - b.rating;
        default:
          return 0;
      }
    });

    setFilteredReviews(result);
    setCurrentPage(1);
  }, [reviews, ratingFilter, sortBy]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
    setRatingFilter('');
    setSortBy('newest');
  };

  const handleApprove = async (reviewId) => {
    const review = reviews.find(r => r.reviewID === reviewId);
    const isReapproving = review?.status === 'Rejected';
    
    const result = await Swal.fire({
      title: isReapproving ? 'Re-approve Review?' : 'Approve Review?',
      text: isReapproving 
        ? 'Are you sure you want to approve this previously rejected review? It will become visible to all users.'
        : 'Are you sure you want to approve this review? It will become visible to all users.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4caf50',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Approve',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await authFetch(`${API_BASE_URL}/Reviews/${reviewId}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchStats();
        await fetchReviews();

        await Swal.fire({
          title: 'Approved!',
          text: 'Review has been approved successfully.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to approve review (${response.status})`);
      }
    } catch (err) {
      await Swal.fire({
        title: 'Error',
        text: 'Error approving review: ' + err.message,
        icon: 'error',
        confirmButtonColor: '#0288d1'
      });
    }
  };

  const handleReject = async (reviewId) => {
    const review = reviews.find(r => r.reviewID === reviewId);
    const isRerejecting = review?.status === 'Approved';
    const reason = rejectionReasons[reviewId];

    if (!reason || reason.trim() === '') {
      await Swal.fire({
        title: 'Missing Rejection Reason',
        text: 'Please provide a rejection reason before rejecting the review.',
        icon: 'warning',
        confirmButtonColor: '#0288d1'
      });
      return;
    }

    const result = await Swal.fire({
      title: isRerejecting ? 'Reject Approved Review?' : 'Reject Review?',
      text: isRerejecting
        ? 'Are you sure you want to reject this approved review? It will be removed from public view.'
        : 'Are you sure you want to reject this review? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Reject',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await authFetch(`${API_BASE_URL}/Reviews/${reviewId}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          approve: false,
          rejectionReason: reason
        })
      });

      if (response.ok) {
        setRejectionReasons(prev => {
          const newReasons = { ...prev };
          delete newReasons[reviewId];
          return newReasons;
        });
        await fetchStats();
        await fetchReviews();

        await Swal.fire({
          title: 'Rejected!',
          text: 'Review has been rejected successfully.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to reject review (${response.status})`);
      }
    } catch (err) {
      await Swal.fire({
        title: 'Error',
        text: 'Error rejecting review: ' + err.message,
        icon: 'error',
        confirmButtonColor: '#0288d1'
      });
    }
  };

  const handleRejectionReasonChange = (reviewId, value) => {
    setRejectionReasons(prev => ({
      ...prev,
      [reviewId]: value
    }));
  };

  const clearFilters = () => {
    setRatingFilter('');
    setSortBy('newest');
  };

  const truncateText = (text, maxLength = 500) => {
    if (!text || text.length <= maxLength) {
      return { text, isTruncated: false };
    }

    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    const finalText = lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated;

    return { text: finalText + '...', isTruncated: true };
  };

  const handleShowMoreReview = (review) => {
    setSelectedReviewModal(review);
  };

  const handleCloseReviewModal = () => {
    setSelectedReviewModal(null);
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleCloseImageModal = () => {
    setSelectedImage(null);
  };

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (selectedImage) {
          handleCloseImageModal();
        } else if (selectedReviewModal) {
          handleCloseReviewModal();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [selectedImage, selectedReviewModal]);

  const handleReviewModalOverlayClick = (e) => {
    if (e.target === e.currentTarget && !selectedImage) {
      handleCloseReviewModal();
    }
  };

  const handleImageModalClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseImageModal();
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i}>{i <= rating ? '★' : '☆'}</span>
      );
    }
    return stars;
  };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; 
        } catch (e) {
            return dateString;
        }
    };

  const pageSize = 4;
  const indexOfLastReview = currentPage * pageSize;
  const indexOfFirstReview = indexOfLastReview - pageSize;
  const currentReviews = filteredReviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(filteredReviews.length / pageSize);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="admin-review-list-container">
        <div className="admin-review-list-loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (error && error !== 'Failed to fetch reviews') {
    return (
      <div className="admin-review-list-container">
        <div className="admin-review-list-error">Error: {error}</div>
        <button
          className="admin-review-list-back-btn"
          onClick={() => navigate('/admin/dashboard')}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="admin-review-list-container">
      <div className="admin-review-list-header">
        <button
          className="admin-review-list-back-btn"
          onClick={() => navigate('/admin/dashboard')}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      <div className="admin-review-list-title">
        <h1>Review Management</h1>
      </div>

      <div className="admin-review-stats">
        <div className="admin-review-stat-card">
          <div className="admin-review-stat-number stat-pending">{stats.pending}</div>
          <div className="admin-review-stat-label">Pending Reviews</div>
        </div>
        <div className="admin-review-stat-card">
          <div className="admin-review-stat-number stat-approved">{stats.approved}</div>
          <div className="admin-review-stat-label">Approved</div>
        </div>
        <div className="admin-review-stat-card">
          <div className="admin-review-stat-number stat-rejected">{stats.rejected}</div>
          <div className="admin-review-stat-label">Rejected</div>
        </div>
        <div className="admin-review-stat-card">
          <div className="admin-review-stat-number stat-total">{stats.total}</div>
          <div className="admin-review-stat-label">Total Reviews</div>
        </div>
        <div className="admin-review-stat-card">
          <div className="admin-review-stat-number stat-average">
            {stats.averageRating} <span style={{ fontSize: '20px' }}>★</span>
          </div>
          <div className="admin-review-stat-label">Average Rating</div>
        </div>
      </div>

      <div className="admin-review-list-tabs">
        <button
          className={`admin-review-list-tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => handleTabChange('pending')}
        >
          Pending
          {stats.pending > 0 && (
            <span className="admin-review-tab-badge">{stats.pending}</span>
          )}
        </button>
        <button
          className={`admin-review-list-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => handleTabChange('all')}
        >
          All Reviews
        </button>
        <button
          className={`admin-review-list-tab ${activeTab === 'approved' ? 'active' : ''}`}
          onClick={() => handleTabChange('approved')}
        >
          Approved
        </button>
        <button
          className={`admin-review-list-tab ${activeTab === 'rejected' ? 'active' : ''}`}
          onClick={() => handleTabChange('rejected')}
        >
          Rejected
        </button>
      </div>

      <div className="admin-review-list-filter">
        <div className="admin-review-list-filter-group">
          <label>Rating</label>
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
          >
            <option value="">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </div>
        <div className="admin-review-list-filter-group">
          <label>Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
        </div>
        <button
          className="admin-review-list-filter-btn"
          onClick={clearFilters}
        >
          Search        
        </button>
      </div>

      <div className="admin-review-list-cards">
        {currentReviews.length > 0 ? (
          currentReviews.map((review) => (
            <div key={review.reviewID} className="admin-review-card">
              <div className="admin-review-card-header">
                <div className="admin-review-user-info">
                  <div className="admin-review-user-details">
                    <div className="admin-review-username">
                      {review.author?.firstName} {review.author?.lastName}
                    </div>
                    <div className="admin-review-email">
                      @{review.author?.username} • {review.author?.email}
                    </div>
                    <div className="admin-review-user-id">
                      User ID: {review.author?.userId}
                    </div>
                  </div>
                </div>
                <div className="admin-review-meta">
                  <div className="admin-review-date-status">
                    <div className="admin-review-date">
                      {formatDate(getDisplayDate(review))}
                    </div>
                    <span className={`admin-review-status admin-review-status-${review.status.toLowerCase()}`}>
                      {review.status}
                    </span>
                  </div>
                  <div className="admin-review-stars-container">
                    {shouldShowUpdatedBadge(review) && (
                      <span className="admin-review-updated-badge">Updated by user</span>
                    )}
                    <span className="admin-review-stars">
                      {renderStars(review.rating)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="admin-review-content">
                <h3 className="admin-review-title">{review.title}</h3>
                <p className="admin-review-text">
                  {(() => {
                    const { text, isTruncated } = truncateText(review.content);
                    return (
                      <>
                        {text}
                        {isTruncated && (
                          <button
                            className="admin-review-more-btn"
                            onClick={() => handleShowMoreReview(review)}
                          >
                            More
                          </button>
                        )}
                      </>
                    );
                  })()}
                </p>

                {review.images && review.images.length > 0 && (
                  <div className="admin-review-images">
                    {review.images.map((image) => (
                      <img
                        key={image.imageId}
                        src={image.imageUrl}
                        alt={`Review image ${image.displayOrder}`}
                        className="admin-review-image"
                        onClick={() => handleImageClick(image.imageUrl)}
                      />
                    ))}
                  </div>
                )}

                {review.rejectionReason && (
                  <div className="admin-review-rejection">
                    <div className="admin-review-rejection-label">
                      Internal Rejection Reason:
                    </div>
                    <div className="admin-review-rejection-text">
                      {review.rejectionReason}
                    </div>
                  </div>
                )}
              </div>
              
              {review.approver && (
                <div className="admin-review-approved-by">
                  {review.status === 'Approved' ? 'Approved' : 'Rejected'} by {review.approver.username} on {formatDate(review.approvedAt)}
                </div>
              )}
              
              {/* Show action buttons based on current status */}
              {review.status === 'Pending' && (
                <div className="admin-review-actions">
                  <button
                    className="admin-review-btn admin-review-btn-approve"
                    onClick={() => handleApprove(review.reviewID)}
                  >
                    <i className="fas fa-check"></i> Approve
                  </button>
                  <input
                    type="text"
                    className="admin-review-rejection-input"
                    placeholder="Enter rejection reason (internal note)..."
                    value={rejectionReasons[review.reviewID] || ''}
                    onChange={(e) => handleRejectionReasonChange(review.reviewID, e.target.value)}
                  />
                  <button
                    className="admin-review-btn admin-review-btn-reject"
                    onClick={() => handleReject(review.reviewID)}
                  >
                    <i className="fas fa-times"></i> Reject
                  </button>
                </div>
              )}
              
              {review.status === 'Approved' && (
                <div className="admin-review-actions">
                  <input
                    type="text"
                    className="admin-review-rejection-input"
                    placeholder="Enter rejection reason (internal note)..."
                    value={rejectionReasons[review.reviewID] || ''}
                    onChange={(e) => handleRejectionReasonChange(review.reviewID, e.target.value)}
                  />
                  <button
                    className="admin-review-btn admin-review-btn-reject"
                    onClick={() => handleReject(review.reviewID)}
                  >
                    <i className="fas fa-times"></i> Reject Review
                  </button>
                </div>
              )}
              
              {review.status === 'Rejected' && (
                <div className="admin-review-actions">
                  <button
                    className="admin-review-btn admin-review-btn-approve"
                    onClick={() => handleApprove(review.reviewID)}
                  >
                    <i className="fas fa-check"></i> Approve Review
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="admin-review-list-empty">
            <i className="fas fa-comments"></i>
            <p className="primary-message">
              {activeTab === 'pending'
                ? 'No pending reviews to moderate.'
                : activeTab === 'approved'
                  ? 'No approved reviews yet.'
                  : activeTab === 'rejected'
                    ? 'No rejected reviews.'
                    : 'No reviews found matching your filters.'}
            </p>
            <p className="secondary-message">
              {activeTab === 'pending'
                ? 'All reviews have been processed or no new reviews have been submitted.'
                : 'Try adjusting your filters or check back later.'}
            </p>
          </div>
        )}
      </div>

      {filteredReviews.length > pageSize && (
        <div className="admin-review-list-pagination">
          <button
            className="admin-review-list-pagination-btn"
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            className="admin-review-list-pagination-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {[...Array(totalPages).keys()].map((number) => {
            if (
              number + 1 === 1 ||
              number + 1 === totalPages ||
              (number + 1 >= currentPage - 1 && number + 1 <= currentPage + 1)
            ) {
              return (
                <button
                  key={number + 1}
                  className={`admin-review-list-pagination-btn ${currentPage === number + 1 ? 'active' : ''
                    }`}
                  onClick={() => paginate(number + 1)}
                >
                  {number + 1}
                </button>
              );
            }

            if (
              (number + 1 === currentPage - 2 && currentPage > 3) ||
              (number + 1 === currentPage + 2 && currentPage < totalPages - 2)
            ) {
              return (
                <span key={number + 1} className="admin-review-list-pagination-btn">
                  ...
                </span>
              );
            }
            return null;
          })}

          <button
            className="admin-review-list-pagination-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-right"></i>
          </button>
          <button
            className="admin-review-list-pagination-btn"
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      )}

      {/* Review Detail Modal */}
      {selectedReviewModal && (
        <div
          className={`admin-review-modal-overlay ${selectedImage ? 'dimmed' : ''}`}
          onClick={handleReviewModalOverlayClick}
        >
          <div className="admin-review-modal">
            <div className="admin-review-modal-header">
              <button
                className="admin-review-modal-close"
                onClick={handleCloseReviewModal}
              >
                <i className="fas fa-times"></i>
              </button>

              <div className="admin-review-modal-user-info">
                <div className="admin-review-modal-user-details">
                  <h4>
                    {selectedReviewModal.author?.firstName} {selectedReviewModal.author?.lastName}
                  </h4>
                  <div className="admin-review-email">
                    @{selectedReviewModal.author?.username} • {selectedReviewModal.author?.email}
                  </div>
                  <div className="admin-review-user-id">
                    User ID: {selectedReviewModal.author?.userId}
                  </div>
                </div>
              </div>

              <div className="admin-review-modal-meta">
                <div className="admin-review-modal-date-status">
                  <div className="admin-review-date">
                    {formatDate(getDisplayDate(selectedReviewModal))}
                  </div>
                  <span className={`admin-review-status admin-review-status-${selectedReviewModal.status.toLowerCase()}`}>
                    {selectedReviewModal.status}
                  </span>
                </div>
                <div className="admin-review-modal-rating">
                  {shouldShowUpdatedBadge(selectedReviewModal) && (
                    <span className="admin-review-updated-badge admin-review-updated-badge-modal">Updated by user</span>
                  )}
                  <div className="admin-review-modal-stars">
                    {renderStars(selectedReviewModal.rating)}
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-review-modal-content">
              <h3 className="admin-review-modal-title">
                {selectedReviewModal.title}
              </h3>
              <p className="admin-review-modal-text">
                {selectedReviewModal.content}
              </p>

              {selectedReviewModal.images && selectedReviewModal.images.length > 0 && (
                <div className="admin-review-modal-images">
                  {selectedReviewModal.images.map((image) => (
                    <img
                      key={image.imageId}
                      src={image.imageUrl}
                      alt={`Review image ${image.displayOrder}`}
                      className="admin-review-modal-image"
                      onClick={() => handleImageClick(image.imageUrl)}
                    />
                  ))}
                </div>
              )}

              {selectedReviewModal.rejectionReason && (
                <div className="admin-review-modal-rejection">
                  <div className="admin-review-modal-rejection-label">
                    Internal Rejection Reason:
                  </div>
                  <div className="admin-review-modal-rejection-text">
                    {selectedReviewModal.rejectionReason}
                  </div>
                </div>
              )}

              {selectedReviewModal.approver && (
                <div className="admin-review-modal-approved-by">
                  {selectedReviewModal.status === 'Approved' ? 'Approved' : 'Rejected'} by {selectedReviewModal.approver.username} on {formatDate(selectedReviewModal.approvedAt)}
                </div>
              )}

              {selectedReviewModal.status === 'Pending' && (
                <div className="admin-review-modal-actions">
                  <button
                    className="admin-review-btn admin-review-btn-approve"
                    onClick={() => {
                      handleApprove(selectedReviewModal.reviewID);
                      handleCloseReviewModal();
                    }}
                  >
                    <i className="fas fa-check"></i> Approve
                  </button>
                  <input
                    type="text"
                    className="admin-review-rejection-input"
                    placeholder="Enter rejection reason (internal note)..."
                    value={rejectionReasons[selectedReviewModal.reviewID] || ''}
                    onChange={(e) => handleRejectionReasonChange(selectedReviewModal.reviewID, e.target.value)}
                  />
                  <button
                    className="admin-review-btn admin-review-btn-reject"
                    onClick={() => {
                      handleReject(selectedReviewModal.reviewID);
                      handleCloseReviewModal();
                    }}
                  >
                    <i className="fas fa-times"></i> Reject
                  </button>
                </div>
              )}
              
              {selectedReviewModal.status === 'Approved' && (
                <div className="admin-review-modal-actions">
                  <input
                    type="text"
                    className="admin-review-rejection-input"
                    placeholder="Enter rejection reason (internal note)..."
                    value={rejectionReasons[selectedReviewModal.reviewID] || ''}
                    onChange={(e) => handleRejectionReasonChange(selectedReviewModal.reviewID, e.target.value)}
                  />
                  <button
                    className="admin-review-btn admin-review-btn-reject"
                    onClick={() => {
                      handleReject(selectedReviewModal.reviewID);
                      handleCloseReviewModal();
                    }}
                  >
                    <i className="fas fa-times"></i> Reject Review
                  </button>
                </div>
              )}
              
              {selectedReviewModal.status === 'Rejected' && (
                <div className="admin-review-modal-actions">
                  <button
                    className="admin-review-btn admin-review-btn-approve"
                    onClick={() => {
                      handleApprove(selectedReviewModal.reviewID);
                      handleCloseReviewModal();
                    }}
                  >
                    <i className="fas fa-check"></i> Approve Review
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="admin-review-image-modal"
          onClick={handleImageModalClick}
        >
          <img src={selectedImage} alt="Review preview" />
        </div>
      )}
    </div>
  );
};

export default AdminReviewManagement;